/**
 * traderReset.js — Client-side ESO trader reset helpers (#283).
 *
 * NA guild traders reset Tuesday 3 PM Eastern Time (DST-aware):
 *   Summer (EDT = UTC-4): Tuesday 19:00 UTC
 *   Winter (EST = UTC-5): Tuesday 20:00 UTC
 *
 * Mirrors `src/utils/time.js#getTraderReset` so v2 widgets can format
 * trade-week dates and snap user-entered ranges to the nearest reset
 * boundary without a server round-trip. ESO Clock (#287) and the
 * Phase-3 kiosk consolidation epic both want this — keep it here.
 *
 * All functions take/return Unix milliseconds unless noted.
 */

const DAY_MS = 86_400_000;
const WEEK_MS = 7 * DAY_MS;

/**
 * Returns the UTC ms for the 3 PM ET reset on the Tuesday whose UTC
 * date matches the given Date. DST-aware: probes 19:00 UTC and falls
 * back to 20:00 UTC outside DST.
 * @param {Date} tuesdayUtcDate
 * @returns {number}
 */
function tuesdayResetUtcMs(tuesdayUtcDate) {
  const y = tuesdayUtcDate.getUTCFullYear();
  const m = String(tuesdayUtcDate.getUTCMonth() + 1).padStart(2, '0');
  const d = String(tuesdayUtcDate.getUTCDate()).padStart(2, '0');
  const probe = new Date(`${y}-${m}-${d}T19:00:00Z`);
  let etHour;
  try {
    etHour = parseInt(new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York', hour: 'numeric', hour12: false,
    }).format(probe), 10);
  } catch {
    etHour = 15; // assume DST when Intl/timezone data is unavailable
  }
  return etHour === 15
    ? probe.getTime()
    : new Date(`${y}-${m}-${d}T20:00:00Z`).getTime();
}

/**
 * Returns the trade-week start (most recent reset that is <= ts).
 * @param {number} [tsMs] defaults to Date.now()
 * @returns {number}
 */
export function currentResetMs(tsMs = Date.now()) {
  const now = new Date(tsMs);
  const utcDay = now.getUTCDay(); // 0=Sun … 6=Sat
  const daysSinceTuesday = utcDay === 0 ? 5 : utcDay === 1 ? 6 : utcDay - 2;
  const lastTuesday = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - daysSinceTuesday,
  ));
  const reset = tuesdayResetUtcMs(lastTuesday);
  return tsMs >= reset ? reset : reset - WEEK_MS;
}

/**
 * Returns the reset N whole trade weeks ago. weeksAgo=0 → start of the
 * current week, weeksAgo=1 → start of last week, etc.
 * @param {number} weeksAgo
 * @param {number} [tsMs]
 * @returns {number}
 */
export function resetWeeksAgo(weeksAgo, tsMs = Date.now()) {
  return currentResetMs(tsMs) - weeksAgo * WEEK_MS;
}

/**
 * Snap an arbitrary timestamp to a reset boundary.
 *   direction='floor'   → most recent reset <= ts (week start)
 *   direction='ceil'    → next reset >= ts (week end)
 *   direction='nearest' → whichever boundary is closer
 * @param {number} tsMs
 * @param {'floor'|'ceil'|'nearest'} [direction]
 * @returns {number}
 */
export function snapToReset(tsMs, direction = 'nearest') {
  const floor = currentResetMs(tsMs);
  const ceil  = floor + WEEK_MS;
  if (direction === 'floor') return floor;
  if (direction === 'ceil')  return ceil;
  return (tsMs - floor) <= (ceil - tsMs) ? floor : ceil;
}

/**
 * True when ts lies exactly on a reset boundary (within ±5min slop to
 * cover DST-transition seconds and clock skew).
 * @param {number} tsMs
 * @returns {boolean}
 */
export function isResetAligned(tsMs) {
  const floor = currentResetMs(tsMs);
  const slop = 5 * 60_000;
  return Math.abs(tsMs - floor) <= slop || Math.abs(tsMs - (floor + WEEK_MS)) <= slop;
}

/**
 * Returns the last `n` trade-week boundaries, oldest first. Each entry
 * is { startMs, endMs, weeksAgo, label } where label is short ET text
 * like "May 5 – May 12".
 * @param {number} n
 * @param {number} [tsMs]
 * @returns {Array<{startMs:number,endMs:number,weeksAgo:number,label:string}>}
 */
export function recentResets(n, tsMs = Date.now()) {
  const out = [];
  for (let w = n - 1; w >= 0; w--) {
    const startMs = resetWeeksAgo(w, tsMs);
    const endMs = startMs + WEEK_MS;
    out.push({ startMs, endMs, weeksAgo: w, label: formatRange(startMs, endMs) });
  }
  return out;
}

/**
 * Format a [startMs, endMs] trade-week as "May 5 – May 12" in ET so the
 * dates match what players see in-game.
 */
export function formatRange(startMs, endMs) {
  const fmt = { month: 'short', day: 'numeric', timeZone: 'America/New_York' };
  const a = new Date(startMs).toLocaleDateString('en-US', fmt);
  // endMs is the *next* reset (exclusive); display the day before.
  const b = new Date(endMs - DAY_MS).toLocaleDateString('en-US', fmt);
  return `${a} – ${b}`;
}

export const RESET_WEEK_MS = WEEK_MS;
